// shop-frontend/src/App.js
import React from "react";
import {
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
  useParams,
} from "react-router-dom";

import ShopProtectedRoute from "./components/ShopProtectedRoute";
import ShopLayout from "./components/ShopLayout";
import { useShopAuth } from "./context/ShopAuthContext";

// auth pages
import ShopAuth from "./pages/ShopAuth";
import ShopLanding from "./pages/ShopLanding"; // ✅ Landing page
import ShopWhy from "./pages/ShopWhy"; // ✅ Why Moondala page
import ShopMerchantAgreement from "./pages/ShopMerchantAgreement"; // ✅ Merchant (seller) legal agreement

// ✅ Token handler for cross-domain auth
import TokenHandler from "./components/TokenHandler";

// ✅ Push notifications
import PushNotificationPrompt from "./components/PushNotificationPrompt";

// protected pages
import ShopFeed from "./pages/ShopFeed";
// import ShopProfile from "./pages/ShopProfile"; // redirected to ShopSettings
import ShopDashboard from "./pages/ShopDashboard";
// ShopFriends removed - duplicate of ShopCustomers (Customers & Followers page)
import ShopSettings from "./pages/ShopSettings";
import AddProduct from "./pages/AddProduct";
import MyProducts from "./pages/MyProducts";
import ShopMessages from "./pages/ShopMessages";
import ShopNotifications from "./pages/ShopNotifications";
import ShopOrders from "./pages/ShopOrders";
import ShopEarnings from "./pages/ShopEarnings";
import ShopCustomers from "./pages/ShopCustomers";
import ShopReturns from "./pages/ShopReturns";
import ShopCartWatchers from "./pages/ShopCartWatchers";
import ShopReturnPolicy from "./pages/ShopReturnPolicy";
import ShopProductViews from "./pages/ShopProductViews";
import ShopShipping from "./pages/ShopShipping";
import ShopWithdrawal from "./pages/ShopWithdrawal";

// shop mall pages
import ShopMallCategory from "./pages/ShopMallCategory";
import ShopMallDeals from "./pages/ShopMallDeals";

// ✅ NEW: Mall Preview page (users view)
import ShopMallPreview from "./pages/ShopMallPreview";

// ✅ Mall Edit page (shop owner edits products)
import ShopMallEdit from "./pages/ShopMallEdit";

// ✅ Mall Product Detail page (themed product view)
import ShopMallProductDetail from "./pages/ShopMallProductDetail";

// ✅ Product details (shop)
import ProductDetailsUnified from "./pages/ProductDetailsUnified";

// ✅ Edit product page
import ShopEditProduct from "./pages/ShopEditProduct";

// ✅ NEW: Shop management pages
import ShopBulkUpload from "./pages/ShopBulkUpload";
import ShopReviewsPage from "./pages/ShopReviewsPage";
import ShopLowStock from "./pages/ShopLowStock";
import ShopStripeReturn from "./pages/ShopStripeReturn";
import ShopStripeRefresh from "./pages/ShopStripeRefresh";

/* =========================
   Token helper
   ========================= */
function hasShopToken() {
  try {
    return !!(
      localStorage.getItem("shopToken") ||
      localStorage.getItem("token") ||
      localStorage.getItem("shop_token")
    );
  } catch {
    return false;
  }
}

/* =========================
   ✅ ROOT ROUTE (re-evaluates on each render)
   ========================= */
function RootRoute() {
  // Use auth context to avoid stale localStorage checks and race conditions
  const { loading, isAuthed } = useShopAuth();
  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  return isAuthed ? <Navigate to="/shop/feed" replace /> : <Navigate to="/shop/login" replace />;
}

/* =========================
   404
   ========================= */
function NotFound() {
  const loc = useLocation();
  return (
    <div style={{ padding: 24 }}>
      <h2>❌ 404 Page Not Found</h2>
      <p>No route matched:</p>
      <pre>{loc.pathname}</pre>
      <Link to="/">Go Home</Link>
    </div>
  );
}

/* =========================
   🔒 Force legacy product URLs → /shop/product/:id
   ========================= */
function ForceShopProductRoute() {
  const params = useParams();
  const { isAuthed } = useShopAuth();
  const authed = !!isAuthed;

  const rawId = params.id || params.productId || params.pid || "";
  const productId = String(rawId || "")
    .replace(/[<>]/g, "")
    .replace(/^:/, "")
    .trim();

  if (!productId) {
    return <Navigate to={authed ? "/shop/feed" : "/shop/login"} replace />;
  }

  if (!authed) {
    return <Navigate to="/shop/login" replace />;
  }

  return <Navigate to={`/shop/product/${encodeURIComponent(productId)}`} replace />;
}

/* =========================
   ✅ FIX: If app opens "/<shopId>" (ObjectId),
   redirect to real mall preview
   Example: /6956c8a1da675c052bd3151  ->  /shop/mall/preview
   ========================= */
function MaybeShopIdRootRedirect() {
  const { maybeId } = useParams();
  const { isAuthed } = useShopAuth();
  const authed = !!isAuthed;

  const raw = String(maybeId || "")
    .replace(/[<>]/g, "")
    .replace(/^:/, "")
    .trim();

  const isObjectId = /^[a-fA-F0-9]{24}$/.test(raw);

  // ✅ only hijack if it's literally an ObjectId
  if (isObjectId) {
    return <Navigate to={authed ? "/shop/mall/preview" : "/shop/login"} replace />;
  }

  // ✅ otherwise: real 404 (do NOT redirect to feed)
  return <NotFound />;
}


export default function App() {
  return (
    <TokenHandler>
      <Routes>
        {/* ✅ Landing page - explains why Moondala is different */}
        <Route path="/shop/landing" element={<ShopLanding />} />
        
        {/* ✅ Why Moondala page - detailed info about benefits */}
        <Route path="/shop/why" element={<ShopWhy />} />

        {/* ✅ Merchant Agreement - public, linked from registration */}
        <Route path="/shop/merchant-agreement" element={<ShopMerchantAgreement />} />

        {/* Default - re-evaluate auth on each render */}
        <Route path="/" element={<RootRoute />} />

      {/* ✅ FIX: support old/top-level paths that your buttons may use */}
      <Route path="/my-products" element={<Navigate to="/shop/my-products" replace />} />
      <Route path="/add-product" element={<Navigate to="/shop/add-product" replace />} />
      <Route path="/messages" element={<Navigate to="/shop/messages" replace />} />
      <Route
        path="/notifications"
        element={<Navigate to="/shop/notifications" replace />}
      />

      {/* ✅ Public Shop Mall Preview (viewable by anyone, no sidebar) */}
      <Route path="/mall/preview" element={<ShopMallPreview hideEditControls={true} />} />
      
      {/* ✅ Mall Product Detail (themed product view for mall visitors) */}
      <Route path="/mall/preview/product/:productId" element={<ShopMallProductDetail />} />
      
      {/* ✅ Shop owner mall preview (same layout as public, but with edit controls) */}
      <Route path="/shop/mall/preview" element={<ShopMallPreview hideEditControls={false} />} />
      
      {/* ✅ Shop owner edit page (select and organize products) */}
      <Route path="/shop/mall/edit" element={<ShopProtectedRoute><ShopMallEdit /></ShopProtectedRoute>} />
      <Route path="/shop/stripe/return" element={<ShopProtectedRoute><ShopStripeReturn /></ShopProtectedRoute>} />
      <Route path="/shop/stripe/refresh" element={<ShopProtectedRoute><ShopStripeRefresh /></ShopProtectedRoute>} />

      {/* Auth - unified login/register page */}
      <Route
        path="/shop/login"
        element={hasShopToken() ? <Navigate to="/shop/feed" replace /> : <ShopAuth />}
      />
      <Route
        path="/shop/register"
        element={<Navigate to="/shop/login?mode=register" replace />}
      />

      {/* ✅ Removed: Waitlist page - shops now register directly */}
      <Route path="/shop-coming-soon" element={<Navigate to="/shop/login" replace />} />

      {/* Legacy / random product URLs → force shop product */}
      <Route path="/product/:productId" element={<ForceShopProductRoute />} />
      <Route path="/products/:productId" element={<ForceShopProductRoute />} />
      <Route path="/public/product/:productId" element={<ForceShopProductRoute />} />
      <Route path="/public/products/:productId" element={<ForceShopProductRoute />} />
      <Route path="/s/:shopId/p/:productId" element={<ForceShopProductRoute />} />
      <Route path="/s/:shopId/product/:productId" element={<ForceShopProductRoute />} />
      <Route path="/s/:shopId/products/:productId" element={<ForceShopProductRoute />} />
      <Route path="/s/:shopId/item/:productId" element={<ForceShopProductRoute />} />
      <Route path="/s/:shopId/items/:productId" element={<ForceShopProductRoute />} />

      {/* ✅ Protected shop area */}
      <Route path="/shop" element={<ShopProtectedRoute />}>
        <Route element={<ShopLayout />}>
          <Route index element={<Navigate to="/shop/dashboard" replace />} />

          <Route path="dashboard" element={<ShopDashboard />} />
          <Route path="orders" element={<ShopOrders />} />
          <Route path="customers" element={<ShopCustomers />} />
          <Route path="returns" element={<ShopReturns />} />
          <Route path="return-policy" element={<ShopReturnPolicy />} />
          <Route path="cart-watchers" element={<ShopCartWatchers />} />
          <Route path="views" element={<ShopProductViews />} />
          <Route path="feed" element={<ShopFeed />} />
          <Route path="profile" element={<Navigate to="/shop/settings" replace />} />
          {/* /shop/friends route removed - use /shop/customers instead (Customers & Followers page) */}
          <Route path="settings" element={<ShopSettings />} />
          <Route path="earnings" element={<ShopEarnings />} />
          <Route path="shipping" element={<ShopShipping />} />
          <Route path="withdrawal" element={<ShopWithdrawal />} />

          {/* ✅ Products */}
          <Route path="products" element={<MyProducts />} />
          <Route path="my-products" element={<Navigate to="/shop/products" replace />} />
          <Route path="add-product" element={<AddProduct />} />
          <Route path="bulk-upload" element={<ShopBulkUpload />} />

          {/* ✅ Reviews management */}
          <Route path="reviews" element={<ShopReviewsPage />} />

          {/* ✅ Low stock alerts */}
          <Route path="low-stock" element={<ShopLowStock />} />

          <Route path="messages" element={<ShopMessages />} />
          <Route path="notifications" element={<ShopNotifications />} />

          {/* Mall */}
          {/* Old mall page retired (its save re-injected a featured_products section) → mall editor */}
          <Route path="mall" element={<Navigate to="/shop/mall/edit" replace />} />
          <Route path="mall/category/:categoryId" element={<ShopMallCategory />} />
          <Route path="mall/deals" element={<ShopMallDeals />} />

          {/* ✅ Product details */}
          <Route path="product/:id" element={<ProductDetailsUnified mode="shop" />} />
          <Route path="mall/product/:id" element={<ProductDetailsUnified mode="shop" />} />

          {/* ✅ Edit product */}
          <Route path="dashboard/edit-product/:id" element={<ShopEditProduct />} />
        </Route>
      </Route>

      {/* ✅ IMPORTANT: this must be AFTER /shop routes */}
      <Route path="/:maybeId" element={<MaybeShopIdRootRedirect />} />

      {/* Catch all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    <PushNotificationPrompt />
    </TokenHandler>
  );
}
